import * as THREE from 'three';
import { state } from './state.js';
import { trackEvent } from './supabase-client.js';

/* ═══════════════════════════════════════════
   § 7. RAYCASTER & INTERACTIVITY (Click & Zoom)
   ═══════════════════════════════════════════ */

// ── Easing functions ───────────────────────
// Exported for render-loop.js to use in the animation tick.

// GSAP-style Exponential Ease Out for buttery smooth camera landings
export function easeOutExpo(x) {
  return x === 1 ? 1 : 1 - Math.pow(2, -10 * x);
}
// Soft ease in-out
export function easeInOutCubic(x) {
  return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
}

// ── flyTo ──────────────────────────────────
export function flyTo(plotName, ctx) {
  const { camera, controls } = ctx;
  const meshes = state.plotMeshes.get(plotName);
  if (!meshes || meshes.length === 0) return;

  // Calculate center of the clicked plot
  const pBox = new THREE.Box3();
  meshes.forEach(m => pBox.expandByObject(m));
  const pCenter = pBox.getCenter(new THREE.Vector3());
  const pSize   = pBox.getSize(new THREE.Vector3());

  state.startCamPos.copy(camera.position);
  state.startCtrlTarget.copy(controls.target);
  state.startCamQuat.copy(camera.quaternion);

  // Look directly at plot
  state.animTarget       = pCenter.clone();
  state.selectedPlotCenter = pCenter.clone();

  // ── Rotation-Preserving Zoom ──
  // Keep the user's current yaw (horizontal orbit angle)
  const currentAzimuth = controls.getAzimuthalAngle();
  const maxDim   = Math.max(pSize.x, pSize.y, pSize.z);
  let zoomDist = Math.max(maxDim * 3, 60); // Pull-back distance

  // Tablet Portrait Mode Aspect Ratio Multiplier
  const isTabletPortrait = window.innerWidth >= 768 && window.innerWidth <= 1024 && window.innerHeight > window.innerWidth;
  if (isTabletPortrait) {
    zoomDist *= 1.3; // Increase distance by 30% to prevent map edges being cut off
  }

  const pitchAngle = Math.PI / 5; // ~36° above horizon (fixed, gentle pitch)

  // Spherical offset from the plot center, preserving user's yaw
  const offsetX = zoomDist * Math.sin(currentAzimuth) * Math.cos(pitchAngle);
  const offsetY = zoomDist * Math.sin(pitchAngle);
  const offsetZ = zoomDist * Math.cos(currentAzimuth) * Math.cos(pitchAngle);

  state.animCameraTarget = pCenter.clone().add(new THREE.Vector3(offsetX, offsetY, offsetZ));

  // Calculate final quaternion
  const tempCam = camera.clone();
  tempCam.position.copy(state.animCameraTarget);
  tempCam.lookAt(state.animTarget);
  state.endCamQuat.copy(tempCam.quaternion);

  state.animDurationScale  = 1.2;
  controls.enabled         = false; // Disable controls during animation
  state.isAnimatingCamera  = true;
  state.animTime           = 0.01;  // Start animation
}

// ── Pointer event listeners ────────────────
export function initCameraControls(ctx, { selectPlot, deselectAll }) {
  const { camera, renderer } = ctx;
  const raycaster = new THREE.Raycaster();
  const mouse     = new THREE.Vector2();
  let pointerDownPos = { x: 0, y: 0 };

  // Prevent clicks if user is dragging/orbiting
  renderer.domElement.addEventListener('pointerdown', (e) => {
    pointerDownPos = { x: e.clientX, y: e.clientY };
  });

  renderer.domElement.addEventListener('pointerup', (e) => {
    // If moved more than 5px, it's a drag, not a click
    if (Math.abs(e.clientX - pointerDownPos.x) > 5 || Math.abs(e.clientY - pointerDownPos.y) > 5) return;

    // Convert mouse to Normalized Device Coordinates
    mouse.x = (e.clientX / window.innerWidth) * 2 - 1;
    mouse.y = -(e.clientY / window.innerHeight) * 2 + 1;

    raycaster.setFromCamera(mouse, camera);

    // Only raycast against interactive plots
    const interactables = [];
    state.plotMeshes.forEach(meshes => interactables.push(...meshes));

    const intersects = raycaster.intersectObjects(interactables, false);

    if (intersects.length > 0) {
      const hit = intersects[0].object;

      let hitPlotName = null;
      for (const [name, meshes] of state.plotMeshes.entries()) {
        if (meshes.includes(hit)) { hitPlotName = name; break; }
      }

      if (hitPlotName) {
        if (state.sfxAudio) {
          if (state.sfxAudio.isPlaying) state.sfxAudio.stop();
          state.sfxAudio.play();
        }
        const uiPlotId = hitPlotName.replace('MAT_PLOT_', 'Plot-');
        selectPlot(uiPlotId);
        flyTo(hitPlotName, ctx);
        // ANALYTICS: plot_view — fire-and-forget, never awaited
        trackEvent('plot_view', uiPlotId);
      }
    } else {
      deselectAll();
    }
  });
}
